import * as Yup from "yup";
import { getInvalidMessage } from "./getValidationMessage";
import { UAE_CODE } from "../constants";
// "Allowed Special Characters" based on swagger 0.0.21
export const SPECIAL_CHARACTERS_REGEX = /^([a-zA-Z0-9.@#%*()/ -])*$/;
export const SPECIAL_CHARACTERS_NEWLINE_REGEX = /^([a-zA-Z0-9.@#%*(),\n\r/ -])*$/;
export const SPECIAL_CHARACTERS_RO_REMARKS_REGEX = /^([a-zA-Z0-9.@#%*(),\n\r/ -:])*$/;
export const TOTAL_EXPERIENCE_YRS_REGEX = /^([a-zA-Z0-9.,@#%*()/ -])*$/;
export const LICENSE_NUMBER_REGEX = /^([a-zA-Z0-9.@#%*()/-])*$/;
export const USER_NAME_REGEX = /^([a-zA-Z0-9.]){1,65}$/;
export const PASSWORD_REGEX = /^([a-zA-Z0-9#@_]){1,30}$/;
export const NAME_REGEX = /^([a-zA-Z ])+$/;
export const NUMBER_REGEX = /^\d*$/;
export const NUMBER_SLASH_REGEX = /^[0-9/]*$/;
export const DECIMAL_REGEX = /^(\d*\.)?\d+$/;
export const DECIMAL_TWO_DIGIT_DECIMAL_REGEX = /^\d*(\.\d{1,2})?$/;
export const ALPHANUMERIC_REGEX = /^([a-zA-Z0-9-])*$/;
export const DECIMAL_UPTO_TWO_REGEX = /^\d{1,2}(\.\d{1,2})?$/;
export const ALPHANUMERIC_ONLY_REGEX = /^([a-zA-Z0-9])*$/;
export const UAE_MOBILE_PHONE_REGEX = /^\d{9}$/;
export const UAE_LANDLINE_PHONE_REGEX = /^\d{8}$/;
export const UAE_IBAN_REGEX = /^AE\d{21}$/;
export const MIN_NON_UAE_PHONE_LENGTH = 4;
export const MAX_NON_UAE_PHONE_LENGTH = 22;
export const MAX_NAME_IN_BUSINESS_LENGTH = 19;
export const MAX_EXPERIENCE_YEARS_LENGTH = 255;
export const MAX_SOURCE_OF_FUNDS_OTHERS_LENGTH = 50;

export const EMIRATES_ID_REGEX = /^784\d{12}$/;
export const SPACE_OCCUPIED_OTHER_REGEX = /^[a-zA-Z. ]{1,50}$/;
export const POBOX_REGEX = /^([a-zA-Z0-9])*$/;

// eslint-disable-next-line max-len
const PROTOCOL_REGEX = /^(https?:\/\/)?/;
const WWW_REGEX = /(www\.)?/;
// Replace vulnerable regex with a more efficient one that avoids catastrophic backtracking
const DOMAIN_REGEX = /(?:[a-zA-Z0-9](?:(?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)\.)+[a-zA-Z]{2,}/;

export const WEBSITE_REGEX = new RegExp(
  `${PROTOCOL_REGEX.source}${WWW_REGEX.source}${DOMAIN_REGEX.source}`
);

export const GLOBAL_INTERMEDIARY_REGEX = /^[a-zA-Z- 0-9]{20}$/;
export const PERCENTAGE_REGEX = /^(100(\.0{1,2})?|(\d){0,2}(\.\d{1,2})?)$/;
export const PERCENTAGE_REGEX_4DEC = /^(100(\.0{1,2})?|(\d){0,2}(\.\d{1,4})?)$/;

export const ALLOWED_CHAR_REGEX = /[^A-Za-z0-9@#%*(),-._/&?\s]+/;

export const FILE_SIZE = 5 * 1048576;
// todo post live
export const SUPPORTED_FORMATS = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/txt",
  "application/vnd.ms-excel",
  "audio/mpeg",
  "video/mp4",
  // "video/mpeg",
  // "audio/wav",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "message/rfc822",
  "application/vnd.ms-outlook"
];
export const OUTLOOK_MSG_FILE = "msg";

export const isNumeric = n => !isNaN(parseFloat(n)) && isFinite(n);
export const checkIsTrimmed = (value = "") => value.length === value.trim().length;

export const addPhoneNoValidationToYup = () => {
  Yup.addMethod(Yup.string, "phoneNo", function({
    fieldName = "Mobile Number",
    codeFieldName = "countryCode",
    isLandline = false
  }) {
    return this.when(codeFieldName, (countryCode, schema) => {
      const maxLength = MAX_NON_UAE_PHONE_LENGTH - (countryCode ? countryCode.length : 3);
      if (countryCode === UAE_CODE) {
        return schema.matches(
          isLandline ? UAE_LANDLINE_PHONE_REGEX : UAE_MOBILE_PHONE_REGEX,
          getInvalidMessage(fieldName)
        );
      } else {
        return schema
          .matches(NUMBER_REGEX, getInvalidMessage(fieldName))
          .min(MIN_NON_UAE_PHONE_LENGTH, getInvalidMessage(fieldName))
          .max(maxLength, getInvalidMessage(fieldName));
      }
    });
  });
};

export const documentValidationSchema = Yup.object().shape({
  file: Yup.mixed()
    .test("size", "File size exceeded (5Mb maximum)", value => value && value.size <= FILE_SIZE)
    .test(
      "type",
      "Supported formats are PDF, JPG, PNG, Word, EML, MP3, MP4, MSG and Excel",
      value =>
        value &&
        (OUTLOOK_MSG_FILE === value.name.split(".").pop() || SUPPORTED_FORMATS.includes(value.type))
    )
});

/**
 * Prevents the minus sign (-) from being entered in number inputs
 * @param {Event} e - The keyboard event
 */
export const blockMinus = e => {
  if (e.key === "-") {
    e.preventDefault();
  }
};

/**
 * Limits the whole number part of a numeric input to 16 digits
 * @param {Event} e - The input event
 */
export const limitDigits = e => {
  const value = e.target.value;
  // Remove any non-digit characters except decimal point
  const digitsOnly = value.replaceAll(/[^\d.]/g, "");
  // Split by decimal point to handle whole and decimal parts
  const parts = digitsOnly.split(".");

  if (parts[0] && parts[0].length > 16) {
    // Limit whole number part to 16 digits
    parts[0] = parts[0].substring(0, 16);
    e.target.value = parts.join(".");
  }
};

/**
 * Yup validation test: Passport expiry date must not be more than 10 years after issue date
 * @param {Date} expiryDate - The passport expiry date
 * @param {Object} context - Yup test context with parent values
 * @returns {boolean} True if valid
 */
export const passportExpiryNotMoreThan10Years = function(expiryDate) {
  const { passportIssueDate } = this.parent;
  if (!expiryDate || !passportIssueDate) return true;
  const issueDate = new Date(passportIssueDate);
  const maxExpiryDate = new Date(issueDate);
  maxExpiryDate.setFullYear(maxExpiryDate.getFullYear() + 10);
  return expiryDate <= maxExpiryDate;
};

/**
 * Yup validation test: Passport expiry date must be greater than issue date
 * @param {Date} expiryDate - The passport expiry date
 * @param {Object} context - Yup test context with parent values
 * @returns {boolean} True if valid
 */
export const passportExpiryGreaterThanIssue = function(expiryDate) {
  const { passportIssueDate } = this.parent;
  if (!expiryDate || !passportIssueDate) return true;
  return new Date(expiryDate) > new Date(passportIssueDate);
};
